import { Dialog, Menu, Portal } from "@ark-ui/react";
import * as Emotion from "@emotion/react";
import {
  FACILITY_STAFF_TIER,
  FacilityKey,
  FacilityKeySchema,
  Username,
  isEnabled,
  isFacilityKey,
  isOrganizationKey,
} from "@packages/core";
import { Link, Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LuAccessibility,
  LuBell,
  LuChevronDown,
  LuChevronRight,
  LuHeartPulse,
  LuHospital,
  LuHouse,
  LuKeyboard,
  LuLogOut,
  LuMenu,
  LuSettings,
  LuSlidersHorizontal,
  LuUser,
  LuUserCog,
  LuUserX,
} from "react-icons/lu";
import { type Merge } from "type-fest";
import * as v from "valibot";
import {
  AlertPlayer,
  BUTTON_STYLES,
  Reha30LogoA,
  SkeletonScreen,
  TypographyStyle,
  buttonStyles,
  menuStyles,
  typographyStyles,
} from "#components";
import {
  getNativeApplicationEdition,
  useApplicationConfigurationQuery,
  useAuthenticationStore,
  useFacilityQuery,
  useFacilityStaffQuery,
  useNativeApplicationRevisionQuery,
  useOperatorQuery,
  useOrganizationQuery,
  useUsername,
} from "#services";
import { css, cx } from "#styled-system/css";
import { token } from "#styled-system/tokens";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey")({
  component: Component,
  params: {
    parse(parameters) {
      return v.parse(v.object({ facilityKey: FacilityKeySchema() }), parameters);
    },
  },
});

function Component() {
  const { facilityKey } = Route.useParams();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const username = useUsername();
  const logOut = useAuthenticationStore((state) => state.logOut);
  const [open, setOpen] = useState(false);

  const facilityStaffQuery = useFacilityStaffQuery(facilityKey, username);
  const operatorQuery = useOperatorQuery(username);

  const iconClass = css({ height: "21px", width: "auto" });
  const linkClass = css(BUTTON_STYLES.raw({ appearance: "ghost", color: "ghost" }), {
    alignItems: "center",
    borderBottomWidth: "1px",
    borderColor: "border",
    borderStyle: "solid",
    display: "grid",
    gap: "8px",
    gridTemplateColumns: "24px 1fr 24px",
    gridTemplateRows: "auto",
    justifyContent: "space-between",
    paddingX: "16px",
    paddingY: "14px",
    width: "100%",
  });
  const titleClass = css(typographyStyles.raw({ fontSize: "16px", lineHeight: 1 }), {
    flexGrow: 1,
    textAlign: "left",
  });

  const chevron = <LuChevronRight className={css({ height: "16px", width: "auto" })} />;

  const { links1, links2, links3 } = useMemo(
    function () {
      const links1 = [
        {
          icon: <LuAccessibility className={iconClass} />,
          label: t("%ResidentList.0%"),
          to: "/facilities/$facilityKey",
        },
      ];

      if (
        isEnabled(operatorQuery.data) ||
        isOrganizationKey(username) ||
        isFacilityKey(username) ||
        (isEnabled(facilityStaffQuery.data) &&
          facilityStaffQuery.data.tier <= FACILITY_STAFF_TIER.MANAGER)
      ) {
        links1.push({
          icon: <LuHeartPulse className={iconClass} />,
          label: t("%BedAllocation.0%"),
          to: "/facilities/$facilityKey/beds/allocation",
        });
      }

      const links2 = [
        {
          label: t("%BedRegistration.0%"),
          to: "/facilities/$facilityKey/beds",
        },
      ];

      if (
        isEnabled(operatorQuery.data) ||
        isOrganizationKey(username) ||
        isFacilityKey(username) ||
        (isEnabled(facilityStaffQuery.data) &&
          facilityStaffQuery.data.tier <= FACILITY_STAFF_TIER.MANAGER)
      ) {
        links2.push(
          { label: t("%ResidentManagement.0%"), to: "/facilities/$facilityKey/residents" },
          { label: t("%SensorManagement.0%"), to: "/facilities/$facilityKey/sensors" },
        );
      }

      links2.push({ label: t("%GroupManagement.0%"), to: "/facilities/$facilityKey/groups" });

      if (
        isEnabled(operatorQuery.data) ||
        isOrganizationKey(username) ||
        isFacilityKey(username) ||
        (isEnabled(facilityStaffQuery.data) &&
          facilityStaffQuery.data.tier <= FACILITY_STAFF_TIER.ADMINISTRATOR)
      ) {
        links2.push({
          label: t("%StaffManagement.0%"),
          to: "/facilities/$facilityKey/staffs",
        });
      }

      links2.push({
        label: t("%TextToSpeech.0%"),
        to: "/facilities/$facilityKey/text-to-speech",
      });

      const links3 = [];

      if (isEnabled(operatorQuery.data)) {
        links3.push({
          icon: <LuKeyboard className={iconClass} />,
          label: t("%ManagementConsole.0%"),
          to: "/management/system-dashboard",
        });
      }

      return { links1, links2, links3 };
    },
    [facilityStaffQuery.data, iconClass, operatorQuery.data, t, username],
  );

  return (
    <div
      className={css({
        "&:has(> [data-maximized])": {
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          overflow: "clip",
        },
        "& > [data-maximized]": { flexGrow: 1, minHeight: 0, overflowY: "clip" },
        "& > main, & > [data-main]": {
          paddingBottom: {
            smDown: "max(16px, env(safe-area-inset-bottom))",
            sm: "max(32px, env(safe-area-inset-bottom))",
          },
          paddingLeft: {
            smDown: "max(16px, env(safe-area-inset-left))",
            sm: "max(24px, env(safe-area-inset-left))",
          },
          paddingRight: {
            smDown: "max(16px, env(safe-area-inset-right))",
            sm: "max(24px, env(safe-area-inset-right))",
          },
          paddingTop: { smDown: "16px", sm: "32px" },
        },
      })}
    >
      <Emotion.Global
        styles={Emotion.css({ ":root": { backgroundColor: token("colors.default-background") } })}
      />

      <AlertPlayer facilityKey={facilityKey} />

      <div
        className={css({
          alignItems: "center",
          backgroundColor: "background",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          justifyContent: "space-between",
          paddingBottom: { smDown: "8px", sm: "14px" },
          paddingLeft: {
            smDown: "max(16px, env(safe-area-inset-left))",
            sm: "max(24px, env(safe-area-inset-left))",
          },
          paddingRight: {
            smDown: "env(safe-area-inset-right)",
            sm: "max(24px, env(safe-area-inset-right))",
          },
          paddingTop: {
            smDown: "max(8px, env(safe-area-inset-top))",
            sm: "max(14px, env(safe-area-inset-top))",
          },
          position: "sticky",
          top: 0,
          zIndex: "header",
        })}
      >
        <UpdateNotice />

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            hideBelow: "sm",
            justifyContent: "space-between",
            width: "100%",
          })}
        >
          <div className={css({ alignItems: "center", display: "flex", gap: "32px" })}>
            <Link params={{ facilityKey }} to="/facilities/$facilityKey">
              <Reha30LogoA className={css({ height: "27px", width: "auto" })} />
            </Link>
            <div className={css({ alignItems: "center", display: "flex", gap: "16px" })}>
              {links1.map(function ({ label, to }) {
                return (
                  <Link
                    className={TypographyStyle({
                      color: "primary",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: 1,
                    })}
                    key={to}
                    params={{ facilityKey }}
                    to={to}
                  >
                    {label}
                  </Link>
                );
              })}

              <Menu.Root>
                <Menu.Trigger
                  className={css(menuStyles.raw().trigger, {
                    alignItems: "center",
                    display: "flex",
                    gap: "4px",
                  })}
                >
                  <span className={css({ fontSize: "14px", fontWeight: 500, lineHeight: 1 })}>
                    {t("%Settings.0%")}
                  </span>
                  <LuChevronDown className={css({ height: "16px", width: "auto" })} />
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content
                    className={css(menuStyles.raw().content, {
                      color: "#18181B",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: 1,
                    })}
                  >
                    {links2.map(function ({ label, to }) {
                      return (
                        <Menu.Item asChild className={menuStyles().item} key={to} value={to}>
                          <Link params={{ facilityKey }} to={to}>
                            {label}
                          </Link>
                        </Menu.Item>
                      );
                    })}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            </div>
          </div>

          <div>
            <Menu.Root>
              <Menu.Trigger asChild className={menuStyles().trigger}>
                <AccountDisplay facilityKey={facilityKey} username={username} />
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content className={menuStyles().content}>
                  {isEnabled(operatorQuery.data) && (
                    <Menu.Item asChild className={menuStyles().item} value="1">
                      <Link to="/management/system-dashboard">
                        <LuSlidersHorizontal className={css({ height: "16px", width: "auto" })} />
                        <span className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>
                          {t("%ManagementConsole.0%")}
                        </span>
                      </Link>
                    </Menu.Item>
                  )}
                  <Menu.Item
                    className={menuStyles().item}
                    onClick={function () {
                      logOut().then(async function () {
                        await navigate({ to: "/login" });
                      });
                    }}
                    value="2"
                  >
                    <LuLogOut className={css({ height: "16px", width: "auto" })} />
                    <span className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>
                      {t("%Logout.0%")}
                    </span>
                  </Menu.Item>
                  <div
                    className={css({
                      borderColor: "border",
                      borderStyle: "solid",
                      borderTopWidth: "1px",
                    })}
                  />
                  <p
                    className={css({
                      color: "primary",
                      fontSize: "12px",
                      lineHeight: 1,
                      opacity: 0.6,
                      paddingX: "8px",
                      paddingY: "4px",
                    })}
                  >
                    {t("%Version.0%")}&nbsp;{NPM_PACKAGE_VERSION}
                  </p>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </div>
        </div>

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            hideFrom: "sm",
            justifyContent: "space-between",
            width: "100%",
          })}
        >
          <Link params={{ facilityKey }} to="/facilities/$facilityKey">
            <Reha30LogoA className={css({ height: "27px", width: "auto" })} />
          </Link>

          <div className={css({ alignItems: "center", display: "flex", height: "100%" })}>
            <button
              className={css(BUTTON_STYLES.raw({ appearance: "ghost", color: "ghost" }), {
                display: "none",
                height: "100%",
                width: "32px",
              })}
            >
              <LuBell />
            </button>
            <button
              className={css(BUTTON_STYLES.raw({ appearance: "ghost", color: "ghost" }), {
                height: "100%",
                width: "40px",
                paddingRight: "16px",
              })}
              onClick={function () {
                setOpen(true);
              }}
            >
              <LuMenu />
            </button>
          </div>
        </div>

        <Dialog.Root open={open}>
          <Portal>
            <Dialog.Positioner className={css({ inset: 0, position: "fixed", zIndex: "overlay" })}>
              <Dialog.Content
                className={css({
                  backgroundColor: "background",
                  height: "100%",
                  overflow: "auto",
                  width: "100%",
                })}
              >
                <UpdateNotice />

                <div
                  className={css({
                    alignItems: "center",
                    display: "flex",
                    gap: "8px",
                    justifyContent: "space-between",
                    paddingBottom: "8px",
                    paddingLeft: "16px",
                    paddingTop: "max(8px, env(safe-area-inset-top))",
                  })}
                >
                  <Reha30LogoA className={css({ height: "27px", width: "auto" })} />

                  <div className={css({ alignItems: "center", display: "flex", height: "100%" })}>
                    <button
                      className={css(BUTTON_STYLES.raw({ appearance: "ghost" }), {
                        height: "100%",
                        paddingRight: "16px",
                      })}
                      onClick={function () {
                        setOpen(false);
                      }}
                    >
                      {t("%Close.0%")}
                    </button>
                  </div>
                </div>

                <div
                  className={css({
                    borderBottomWidth: "1px",
                    borderColor: "border",
                    borderStyle: "solid",
                    marginTop: "12px",
                    paddingX: "16px",
                    paddingY: "12px",
                    width: "100%",
                  })}
                >
                  <p className={typographyStyles({ fontSize: "12px", lineHeight: 1 })}>
                    {t("%Menu.0%")}
                  </p>
                </div>

                {links1.map(function ({ icon, label, to }) {
                  return (
                    <Link
                      className={linkClass}
                      key={to}
                      onClick={function () {
                        setOpen(false);
                      }}
                      params={{ facilityKey }}
                      to={to}
                    >
                      {icon}
                      <p className={titleClass}>{label}</p>
                      {chevron}
                    </Link>
                  );
                })}

                <div
                  className={css({
                    alignItems: "center",
                    borderBottomWidth: "1px",
                    borderColor: "border",
                    borderStyle: "solid",
                    display: "grid",
                    gap: "8px",
                    gridTemplateColumns: "24px 1fr 24px",
                    gridTemplateRows: "auto",
                    justifyContent: "space-between",
                    paddingX: "16px",
                    paddingY: "14px",
                    width: "100%",
                  })}
                >
                  <LuSettings className={css({ height: "21px", width: "auto" })} />
                  <p className={titleClass}>{t("%Settings.0%")}</p>
                  <div />
                </div>

                {links2.map(function ({ label, to }) {
                  return (
                    <Link
                      className={linkClass}
                      key={to}
                      onClick={function () {
                        setOpen(false);
                      }}
                      params={{ facilityKey }}
                      to={to}
                    >
                      <div />
                      <p className={titleClass}>{label}</p>
                      {chevron}
                    </Link>
                  );
                })}

                {links3.map(function ({ icon, label, to }) {
                  return (
                    <Link
                      className={linkClass}
                      key={to}
                      onClick={function () {
                        setOpen(false);
                      }}
                      to={to}
                    >
                      {icon}
                      <p className={titleClass}>{label}</p>
                      {chevron}
                    </Link>
                  );
                })}

                <div
                  className={css({
                    borderBottomWidth: "1px",
                    borderColor: "border",
                    borderStyle: "solid",
                    marginTop: "12px",
                    paddingX: "16px",
                    paddingY: "12px",
                    width: "100%",
                  })}
                >
                  <p className={typographyStyles({ fontSize: "12px", lineHeight: 1 })}>
                    {t("%Account.1%")}
                  </p>
                </div>

                <AccountDisplay facilityKey={facilityKey} username={username} />
                <div
                  className={css({
                    borderBottomWidth: "1px",
                    borderColor: "border",
                    borderStyle: "solid",
                    width: "100%",
                  })}
                />

                <div className={css({ marginTop: "32px", marginX: "16px" })}>
                  <button
                    className={css(buttonStyles.raw({ color: "secondary" }), { width: "100%" })}
                    onClick={function () {
                      logOut().then(async function () {
                        await navigate({ to: "/login" });
                      });
                    }}
                  >
                    {t("%Logout.0%")}
                  </button>
                </div>

                <p
                  className={css(typographyStyles.raw({ textAlign: "center" }), {
                    color: "primary",
                    fontSize: "12px",
                    lineHeight: 1,
                    marginTop: "8px",
                    opacity: 0.6,
                    paddingX: "8px",
                    paddingY: "4px",
                  })}
                >
                  {t("%Version.0%")}&nbsp;{NPM_PACKAGE_VERSION}
                </p>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      </div>

      <Outlet />
    </div>
  );
}

function UpdateNotice() {
  const { t } = useTranslation();
  const { data: applicationConfiguration } = useApplicationConfigurationQuery();
  const nativeApplicationRevisionQuery = useNativeApplicationRevisionQuery();

  if (!nativeApplicationRevisionQuery.data) {
    return null;
  }

  const requiredApplicationConfiguration =
    applicationConfiguration?.[getNativeApplicationEdition() === "Android" ? "android" : "ios"];
  const requiredRevision = requiredApplicationConfiguration?.requiredRevision ?? 0;
  const outdated = nativeApplicationRevisionQuery.data < requiredRevision;

  if (!outdated) {
    return null;
  }

  return (
    <div
      className={css({
        backgroundColor: "white",
        color: "#18181B",
        paddingX: "16px",
        paddingY: "12px",
        textAlign: "center",
        width: "100%",
      })}
    >
      <p className={TypographyStyle({ fontSize: "14px", fontWeight: 500 })}>
        {t("%SoftwareUpdateNotice.0%")}
      </p>
    </div>
  );
}

type AccountDisplayProperties = Merge<
  React.ComponentProps<"button">,
  { facilityKey: FacilityKey; username?: Username | null }
>;
function AccountDisplay({
  className,
  facilityKey,
  username,
  ...properties
}: AccountDisplayProperties) {
  const { t } = useTranslation();

  const facilityQuery = useFacilityQuery(facilityKey);
  const facilityStaffQuery = useFacilityStaffQuery(facilityKey, username);
  const operatorQuery = useOperatorQuery(username);
  const organizationQuery = useOrganizationQuery(username);

  let icon, name;

  if (operatorQuery.data) {
    icon = <LuUserCog className={css({ height: "90%", strokeWidth: "2.4px", width: "90%" })} />;
    name = operatorQuery.data.name + " (" + t("%Operator.0%") + ")";
  } else if (organizationQuery.data) {
    icon = <LuHospital className={css({ height: "90%", strokeWidth: "2.4px", width: "90%" })} />;
    name = organizationQuery.data.name + " (" + t("%Organization.0%") + ")";
  } else if (facilityStaffQuery.data) {
    icon = <LuUser className={css({ height: "90%", strokeWidth: "2.4px", width: "90%" })} />;
    name =
      facilityStaffQuery.data.name +
      " (" +
      t("%FacilityStaffTier." + facilityStaffQuery.data.tier + ".0%") +
      ")";
  } else if (facilityQuery.data && username === facilityQuery.data.facilityKey) {
    icon = <LuHouse className={css({ height: "90%", strokeWidth: "2.4px", width: "90%" })} />;
    name = facilityQuery.data.name + " (" + t("%Facility.0%") + ")";
  } else {
    icon = <LuUserX className={css({ height: "90%", strokeWidth: "2.4px", width: "90%" })} />;
    name = t("%Unknown.0%");
  }

  const loading =
    facilityQuery.isLoading ||
    facilityStaffQuery.isLoading ||
    operatorQuery.isLoading ||
    organizationQuery.isLoading;

  return (
    <button
      {...properties}
      className={cx(
        css({
          alignItems: "center",
          borderRadius: "4px",
          display: "flex",
          gap: "6px",
          maxWidth: { sm: "208px" },
          paddingX: { smDown: "16px", sm: "8px" },
          paddingY: { smDown: "12px", sm: "8px" },
          textAlign: "left",
          _hover: { backgroundColor: "rgba(0, 0, 0, 0.1)" },
          _focus: { backgroundColor: "rgba(0, 0, 0, 0.1)" },
          _active: { backgroundColor: "rgba(0, 0, 0, 0.25)" },
        }),
        className,
      )}
    >
      {loading ? (
        <SkeletonScreen
          className={css({ borderRadius: "65536px", flexShrink: 0, height: "24px", width: "24px" })}
        />
      ) : (
        <div
          className={css({
            alignItems: "center",
            borderColor: "primary",
            borderRadius: "65536px",
            borderStyle: "solid",
            borderWidth: "2px",
            display: "flex",
            flexShrink: 0,
            height: "24px",
            justifyContent: "center",
            overflow: "clip",
            width: "24px",
          })}
        >
          {icon}
        </div>
      )}

      <div className={css({ minWidth: 0 })}>
        {loading ? (
          <SkeletonScreen className={css({ height: "14px", width: "75%" })} />
        ) : (
          <p
            className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}
            title={name}
          >
            {name}
          </p>
        )}
        {facilityQuery.isLoading ? (
          <SkeletonScreen className={css({ height: "12px", marginTop: "4px", width: "50%" })} />
        ) : (
          <p
            className={TypographyStyle({
              color: "primary/60",
              ellipsis: true,
              fontSize: "12px",
              lineHeight: 1,
              marginTop: "4px",
            })}
          >
            {facilityQuery.data?.name}
          </p>
        )}
      </div>
    </button>
  );
}
